// pages/CrmOverview.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { createAxiosInstance } from '../utils/Axios';
import { useAuth, hasModuleAccess, hasSectionAccess } from '../utils/hooks/useAuth';

// Custom label for XAxis
const CustomTick = (props) => {
  const { x, y, payload } = props;
  const lines = payload.value.split('\n');
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#111827" fontSize={13} fontWeight={500}>{lines[0]}</text>
      <text x={0} y={0} dy={36} textAnchor="middle" fill="#111827" fontSize={16} fontWeight={700}>{lines[1]}</text>
      <text x={0} y={0} dy={52} textAnchor="middle" fill="#6B7280" fontSize={12}>{lines[2]}</text>
    </g>
  );
};

export default function CrmOverview() {
  const [isLoading, setIsLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const { access, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Check module and section permissions
  const hasCrmAccess = access ? hasModuleAccess('iurm', access) : false;
  
  // Check specific section permissions
  const canViewOverview = access ? hasSectionAccess('iurm', 'Overview', 'view', access) : false;
  const canViewLeads = access ? hasSectionAccess('iurm', 'Leads', 'view', access) : false;
  const canViewContacts = access ? hasSectionAccess('iurm', 'Contacts', 'view', access) : false;
  const canViewAccounts = access ? hasSectionAccess('iurm', 'Accounts', 'view', access) : false;
  const canViewTasks = access ? hasSectionAccess('iurm', 'Tasks', 'view', access) : false;
  const canViewCampaigns = access ? hasSectionAccess('iurm', 'Campaigns', 'view', access) : false;
  const canViewIntegration = access ? hasSectionAccess('iurm', 'Integration', 'view', access) : false;

  // Check create permissions
  const canCreateLead = access ? hasSectionAccess('iurm', 'Leads', 'create', access) : false;

  useEffect(() => {
    console.log('CRM Module Access:', hasCrmAccess);
    console.log('Overview View Access:', canViewOverview);
    console.log('Leads View Access:', canViewLeads);
    console.log('Create Lead Permission:', canCreateLead);
  }, [hasCrmAccess, canViewOverview, canViewLeads, canCreateLead]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
        
        // Only fetch data for sections the user has access to
        const promises = [];
        
        if (canViewLeads) {
          promises.push(axiosInstance.get(`${apiUrl}/deals`).catch(() => ({ data: { deals: [] } })));
        } else {
          promises.push(Promise.resolve({ data: { deals: [] } }));
        }
        
        if (canViewAccounts) {
          promises.push(axiosInstance.get(`${apiUrl}/accounts`).catch(() => ({ data: { accounts: [] } })));
        } else {
          promises.push(Promise.resolve({ data: { accounts: [] } }));
        }
        
        if (canViewTasks) {
          promises.push(axiosInstance.get(`${apiUrl}/tasks`).catch(() => ({ data: { tasks: [] } })));
        } else {
          promises.push(Promise.resolve({ data: { tasks: [] } }));
        }
        
        const [dealsRes, accountsRes, tasksRes] = await Promise.all(promises);
        
        setDeals(dealsRes.data?.deals || []);
        setAccounts(accountsRes.data?.accounts || []);
        setTasks(tasksRes.data?.tasks || []);
        
      } catch (error) {
        console.error("Failed to fetch dashboard data", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (hasCrmAccess && canViewOverview) {
      fetchDashboardData();
    } else {
      setIsLoading(false);
      // If user doesn't have overview access, redirect to first available section
      if (hasCrmAccess && !canViewOverview) {
        if (canViewLeads) navigate('/crm/leads');
        else if (canViewContacts) navigate('/crm/contacts');
        else if (canViewAccounts) navigate('/crm/accounts');
        else if (canViewTasks) navigate('/crm/tasks');
        else if (canViewCampaigns) navigate('/crm/campaigns');
      }
    }
  }, [hasCrmAccess, canViewOverview, canViewLeads, canViewAccounts, canViewTasks, canViewCampaigns, navigate]);

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
        Loading permissions...
      </div>
    );
  }

  // Show access denied if no module access
  if (!hasCrmAccess) {
    return (
      <div style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        margin: '20px'
      }}>
        <h2 style={{ color: '#111827', marginBottom: '12px' }}>Access Denied</h2>
        <p style={{ color: '#6B7280', fontSize: '16px' }}>
          You don't have permission to access the CRM module.
          <br />
          Please contact your administrator.
        </p>
      </div>
    );
  }

  // Show access denied if no overview access
  if (!canViewOverview) {
    return (
      <div style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        margin: '20px'
      }}>
        <h2 style={{ color: '#111827', marginBottom: '12px' }}>Access Denied</h2>
        <p style={{ color: '#6B7280', fontSize: '16px' }}>
          You don't have permission to view the Overview section.
          <br />
          Please contact your administrator.
        </p>
      </div>
    );
  }

  // Compute Metrics
  const totalDeals = deals.length;
  const pipelineValue = deals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
  const openDealsList = deals.filter(d => d.stage !== 'Closed Won' && d.stage !== 'Closed Lost');
  const openDealsCount = openDealsList.length;
  const closedWonDeals = deals.filter(d => d.stage === 'Closed Won').length;
  
  const avgDealSize = totalDeals > 0 ? (pipelineValue / totalDeals) : 0;
  const conversionRate = totalDeals > 0 ? ((closedWonDeals / totalDeals) * 100).toFixed(1) : 0;

  // Formatting helpers
  const formatCurrency = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
    return `$${val}`;
  };

  // Prepare Chart Data
  const stages = ['Lead', 'Contact Made', 'Proposal', 'Negotiation', 'Closed Won'];
  const chartData = stages.map(stage => {
    const stageDeals = deals.filter(d => d.stage === stage);
    const count = stageDeals.length;
    const percentage = totalDeals > 0 ? Math.round((count / totalDeals) * 100) : 0;
    
    let displayStage = stage;
    if (stage === 'Contact Made') displayStage = 'Contacted';
    if (stage === 'Closed Won') displayStage = 'Closed';

    return {
      name: `${displayStage}\n\n(${percentage}%)`,
      value: count,
      originalStage: stage
    };
  });

  // Prepare Top Accounts (only if user has accounts access)
  const colors = [
    { bg: '#F3E8FF', text: '#7C3AED' },
    { bg: '#E0E7FF', text: '#4F46E5' },
    { bg: '#FCE7F3', text: '#DB2777' },
    { bg: '#ECFEFF', text: '#0891B2' },
    { bg: '#FEF3C7', text: '#D97706' }
  ];
  
  const topAccounts = canViewAccounts ? accounts.slice(0, 5).map((acc, idx) => ({
    id: acc.name ? acc.name.substring(0, 2).toUpperCase() : 'AC',
    name: acc.name || 'Unknown Account',
    value: acc.annualRevenue ? `$${acc.annualRevenue.toLocaleString()}` : `$${Math.floor(Math.random() * 900) + 100},000`,
    color: colors[idx % colors.length].bg,
    textColor: colors[idx % colors.length].text
  })) : [];

  // Prepare Follow-ups from Tasks (only if user has tasks access)
  const pendingTasks = canViewTasks ? tasks.filter(t => t.status === 'Pending' || t.status === 'In Progress').slice(0, 4).map(t => ({
    company: t.account || t.contact || t.name,
    desc: t.name,
    time: t.date || 'Upcoming'
  })) : [];

  return (
    <div className="crm-leads-container" style={{ padding: '16px', backgroundColor: '#f8f9fa', minHeight: '100%', fontFamily: 'Inter, sans-serif' }}>
      {/* Tabs Section - conditionally render based on permissions */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #E5E7EB', marginBottom: '20px', paddingBottom: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          {canViewOverview && (
            <div className="nav-item active" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px', backgroundColor: '#8B5CF6', color: '#fff' }}>
              <div className="nav-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor' }}></div>
              Overview
            </div>
          )}
          {canViewLeads && (
            <Link to="/crm/leads" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                Leads
              </div> 
            </Link>
          )}
          {canViewContacts && (
            <Link to="/crm/contacts" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer', textDecoration: 'none' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              Contacts
            </Link>
          )}
          {canViewAccounts && (
            <Link to="/crm/accounts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>
                Accounts
              </div>
            </Link>
          )}
          {canViewTasks && (
            <Link to="/crm/tasks" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                Tasks
              </div>
            </Link>
          )}
          {canViewCampaigns && (
            <Link to="/crm/campaigns" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                Campaigns
              </div>
            </Link>
          )}
          {canViewIntegration && (
            <Link to="/crm/integration" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 16V7a2 2 0 0 0-2-2h-4"></path><path d="M4 16V7a2 2 0 0 1 2-2h4"></path><rect x="9" y="15" width="6" height="6" rx="1"></rect><line x1="12" y1="5" x2="12" y2="15"></line></svg>
                Integration
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0', color: '#111827', letterSpacing: '-0.5px' }}>CRM Dashboard</h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#374151', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            Export Report
          </button>
          {canCreateLead && (
            <Link to="/crm/leads" style={{ textDecoration: 'none' }}>
              <button style={{ padding: '8px 20px', backgroundColor: '#5B21B6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                New Lead
              </button>
            </Link>
          )}
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading Dashboard Data...</div>
      ) : (
        <>
          {/* Top Cards */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px', backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ color: '#111827', fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>Pipeline Value</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '12px', letterSpacing: '-1px' }}>{formatCurrency(pipelineValue)}</div>
              <div style={{ fontSize: '14px', color: '#10B981', fontWeight: '600' }}>Active Pipeline</div>
            </div>
            <div style={{ flex: 1, minWidth: '200px', backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ color: '#111827', fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>Conversion Rate</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '12px', letterSpacing: '-1px' }}>{conversionRate}%</div>
              <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>From {totalDeals} Deals</div>
            </div>
            <div style={{ flex: 1, minWidth: '200px', backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ color: '#111827', fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>Open Deals</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '12px', letterSpacing: '-1px' }}>{openDealsCount}</div>
              <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>In Progress</div>
            </div>
            <div style={{ flex: 1, minWidth: '200px', backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ color: '#111827', fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>Avg. Deal Size</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '12px', letterSpacing: '-1px' }}>{formatCurrency(avgDealSize)}</div>
              <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>Overall average</div>
            </div>
          </div>

          {/* Middle Section - only show if user has access to these sections */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
            {/* Chart - only show if user can view leads */}
            {canViewLeads && (
              <div style={{ flex: 2, minWidth: '300px', backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ color: '#111827', fontSize: '16px', fontWeight: 'bold', marginBottom: '24px' }}>Pipeline Overview</div>
                <div style={{ width: '100%', height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 40 }}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={<CustomTick />} 
                        interval={0}
                      />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#8B5CF6" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" dot={{ stroke: '#8B5CF6', strokeWidth: 3, fill: '#fff', r: 6 }} activeDot={{ r: 8, stroke: '#8B5CF6', strokeWidth: 3, fill: '#fff' }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Top Accounts - only show if user has accounts access */}
            {canViewAccounts && (
              <div style={{ flex: 1, minWidth: '250px', backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ color: '#111827', fontSize: '16px', fontWeight: 'bold', marginBottom: '24px' }}>Top Accounts</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {topAccounts.length > 0 ? topAccounts.map(acc => (
                    <div key={acc.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: acc.color, color: acc.textColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 'bold' }}>
                          {acc.id}
                        </div>
                        <span style={{ fontSize: '15px', color: '#111827', fontWeight: '500', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.name}</span>
                      </div>
                      <span style={{ fontSize: '15px', color: '#111827', fontWeight: '600' }}>{acc.value}</span>
                    </div>
                  )) : (
                    <div style={{ color: '#6B7280', fontSize: '14px', textAlign: 'center', padding: '20px' }}>No accounts found.</div>
                  )}
                  <Link to="/crm/accounts" style={{ textDecoration: 'none' }}>
                    <div style={{ color: '#7C3AED', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginTop: '4px' }}>
                      View all accounts &gt;
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Section - only show if user has access */}
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {/* Open Deals - only show if user has leads access */}
            {canViewLeads && (
              <div style={{ flex: 2, minWidth: '300px', backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
                <div style={{ color: '#111827', fontSize: '16px', fontWeight: 'bold', marginBottom: '24px' }}>Open Deals</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ color: '#6B7280', fontSize: '13px', borderBottom: '1px solid #F3F4F6' }}>
                      <th style={{ paddingBottom: '16px', fontWeight: '600' }}>Deal Name</th>
                      <th style={{ paddingBottom: '16px', fontWeight: '600' }}>Account / Contact</th>
                      <th style={{ paddingBottom: '16px', fontWeight: '600' }}>Stage</th>
                      <th style={{ paddingBottom: '16px', fontWeight: '600' }}>Value</th>
                      <th style={{ paddingBottom: '16px', fontWeight: '600' }}>Close Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openDealsList.length > 0 ? openDealsList.slice(0, 5).map((deal, idx) => (
                      <tr key={deal._id || idx} style={{ borderBottom: idx === Math.min(openDealsList.length-1, 4) ? 'none' : '1px solid #F3F4F6' }}>
                        <td style={{ padding: '16px 0', fontSize: '14px', color: '#111827', fontWeight: '600' }}>{deal.name}</td>
                        <td style={{ padding: '16px 0', fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>{deal.accountName || deal.contactName || '-'}</td>
                        <td style={{ padding: '16px 0', fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>{deal.stage}</td>
                        <td style={{ padding: '16px 0', fontSize: '14px', color: '#111827', fontWeight: '600' }}>{deal.amount ? formatCurrency(deal.amount) : '-'}</td>
                        <td style={{ padding: '16px 0', fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>{deal.expectedCloseDate || '-'}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>No open deals found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Follow-ups - only show if user has tasks access */}
            {canViewTasks && (
              <div style={{ flex: 1, minWidth: '250px', backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ color: '#111827', fontSize: '16px', fontWeight: 'bold', marginBottom: '24px' }}>Pending Tasks</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {pendingTasks.length > 0 ? pendingTasks.map((f, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#8B5CF6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', flexShrink: 0 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        </div>
                        <div>
                          <div style={{ fontSize: '15px', color: '#111827', fontWeight: '600', marginBottom: '4px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.company}</div>
                          <div style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.desc}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#6B7280', whiteSpace: 'nowrap', fontWeight: '500' }}>{f.time}</div>
                    </div>
                  )) : (
                    <div style={{ color: '#6B7280', fontSize: '14px', textAlign: 'center', padding: '20px' }}>No pending tasks.</div>
                  )}
                </div>
                <Link to="/crm/tasks" style={{ textDecoration: 'none' }}>
                  <div style={{ color: '#7C3AED', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginTop: '32px' }}>
                    View all tasks &gt;
                  </div>
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}